// Files "uploaded" during a demo session: the presigned PUT is short-circuited
// in services/upload.service.ts, which hands the File to this registry so
// documents attached afterwards can still be opened (as an object URL).

import { ids } from '../core';
import { makeCounter } from './common';

const counter = makeCounter(0);
const fileUrls = new Map<string, string>();
const cachedUrls = new Map<string, string>();

export function newUploadId(): string {
  return ids.upload(counter.next());
}

export function demoUploadUrl(uploadId: string): string {
  return `demo://upload/${uploadId}`;
}

/** Called by upload.service in demo mode instead of PUTting to storage. */
export function registerDemoUpload(presignedUrl: string, file: Blob): void {
  const id = presignedUrl.replace(/^demo:\/\/upload\//, '');
  try {
    fileUrls.set(id, URL.createObjectURL(file));
  } catch {
    // No object URLs (e.g. a test runner): the document simply has no preview.
  }
}

export function uploadedFileUrl(assetId: string): string | null {
  return fileUrls.get(assetId) ?? null;
}

function pdfEscape(s: string): string {
  return s.replace(/[\\()]/g, (c) => `\\${c}`).replace(/[^\x20-\x7e]/g, '');
}

/** A one-page PDF (as an object URL) so seeded agreement documents open in a new tab. */
export function generatedPdfUrl(key: string, title: string, lines: string[]): string | null {
  const cached = cachedUrls.get(key);
  if (cached) return cached;
  const text = [`BT /F1 18 Tf 60 760 Td (${pdfEscape(title)}) Tj ET`]
    .concat(lines.map((line, i) => `BT /F1 11 Tf 60 ${728 - i * 18} Td (${pdfEscape(line)}) Tj ET`))
    .concat([`BT /F1 9 Tf 60 60 Td (Recyclee demo document - sample data only.) Tj ET`])
    .join('\n');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>',
    `<< /Length ${text.length} >>\nstream\n${text}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];
  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((body, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((o) => {
    pdf += `${String(o).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  try {
    const url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
    cachedUrls.set(key, url);
    return url;
  } catch {
    return null;
  }
}

export function resetUploads(): void {
  counter.reset();
  fileUrls.clear();
}
